import { useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, BaggageClaim, ImageIcon, Minus, Plus } from 'lucide-react';
import { useMemo, useState } from 'react';
import { Link, useParams } from 'react-router';
import type { PurchaseOrderItem } from '@/api/hanly-v2/hanlyAPIV2.schemas';
import {
  getPurchaseOrderDetailQueryKey,
  usePurchaseOrderDeliveryCreate,
  usePurchaseOrderDetail,
} from '@/api/hanly-v2/purchase-orders/purchase-orders';
import { getCdnImageUrl } from '@/utils/image';

const ItemCard = ({
  item,
  receivingQty,
  onReceivingQtyChange,
}: {
  item: PurchaseOrderItem;
  receivingQty?: number;
  onReceivingQtyChange?: (qty: number) => void;
}) => {
  const totalQty = Number(item.quantity ?? 0);
  const isReceived = item.delivery != null;

  return (
    <div className="bg-white flex gap-2 items-start p-2">
      {item.thumbnail_url ? (
        <img
          src={getCdnImageUrl(item.thumbnail_url, { width: 160, height: 160 })}
          alt=""
          className="size-20 shrink-0 rounded border border-gray-200 object-cover"
          loading="lazy"
        />
      ) : (
        <div className="size-20 shrink-0 rounded border border-gray-200 flex items-center justify-center bg-gray-100">
          <ImageIcon className="size-6 text-gray-400" />
        </div>
      )}
      <div className="flex-1 flex flex-col min-w-0">
        <p>{item.name}</p>
        <p className="text-sm text-neutral-500">{[item.product_code, item.spec].filter(Boolean).join('/')}</p>
      </div>

      <div className="shrink-0 w-[120px] flex flex-col items-center gap-1">
        {!isReceived && onReceivingQtyChange && (
          <>
            <button
              type="button"
              onClick={() => onReceivingQtyChange(totalQty)}
              className="text-sky-800 text-right w-full cursor-pointer"
            >
              全て入荷
            </button>
            <div className="flex items-center border border-neutral-400 rounded overflow-hidden w-full">
              <button
                type="button"
                onClick={() => onReceivingQtyChange(Math.max(0, (receivingQty ?? 0) - 1))}
                className="bg-green-600 text-white size-7 flex items-center justify-center shrink-0 cursor-pointer"
              >
                <Minus className="size-4" />
              </button>
              <span className="flex-1 text-center text-base">{receivingQty ?? 0}</span>
              <button
                type="button"
                onClick={() => onReceivingQtyChange(Math.min(totalQty, (receivingQty ?? 0) + 1))}
                className="bg-green-600 text-white size-7 flex items-center justify-center shrink-0 cursor-pointer"
              >
                <Plus className="size-4" />
              </button>
            </div>
          </>
        )}
        <div className="flex gap-2 items-center">
          <BaggageClaim className="size-6 shrink-0 text-neutral-500 stroke-1" />
          <p className="text-xl">
            {isReceived ? totalQty : 0}/{totalQty}
          </p>
        </div>
      </div>
    </div>
  );
};

export const ReceivingDetailPage = () => {
  const { id } = useParams<{ id: string }>();
  const orderId = Number(id);
  const isValidOrderId = Number.isInteger(orderId) && orderId > 0;
  const { data: order, isLoading } = usePurchaseOrderDetail(orderId, {
    query: { enabled: isValidOrderId },
  });
  const [receivingQuantities, setReceivingQuantities] = useState<Record<number, number>>({});
  const queryClient = useQueryClient();
  const { mutate: createDelivery, isPending: isDelivering } = usePurchaseOrderDeliveryCreate();

  const { unreceivedItems, receivedItems } = useMemo(() => {
    if (!order) return { unreceivedItems: [], receivedItems: [] };
    const unreceived: PurchaseOrderItem[] = [];
    const received: PurchaseOrderItem[] = [];
    for (const item of order.items) {
      if (item.delivery != null) {
        received.push(item);
      } else {
        unreceived.push(item);
      }
    }
    return { unreceivedItems: unreceived, receivedItems: received };
  }, [order]);

  const updateReceivingQty = (itemId: number, qty: number) => {
    setReceivingQuantities((prev) => ({ ...prev, [itemId]: qty }));
  };

  const handleReceivingComplete = () => {
    const itemsToReceive = unreceivedItems.filter((item) => (receivingQuantities[item.id] ?? 0) > 0);
    if (itemsToReceive.length === 0) return;

    const purchaseOrderItemIds = itemsToReceive.map((item) => item.id);
    const itemQuantities: Record<string, string> = {};
    let hasPartial = false;
    for (const item of itemsToReceive) {
      const receivingQty = receivingQuantities[item.id] ?? 0;
      const totalQty = Number(item.quantity ?? 0);
      if (receivingQty !== totalQty) {
        hasPartial = true;
        itemQuantities[String(item.id)] = String(receivingQty);
      }
    }

    createDelivery(
      {
        data: {
          purchase_order_item_ids: purchaseOrderItemIds,
          ...(hasPartial ? { item_quantities: itemQuantities } : {}),
        },
      },
      {
        onSuccess: () => {
          setReceivingQuantities({});
          queryClient.invalidateQueries({ queryKey: getPurchaseOrderDetailQueryKey(orderId) });
        },
      },
    );
  };

  const hasReceivedItems = unreceivedItems.some((item) => (receivingQuantities[item.id] ?? 0) > 0);

  if (isLoading) {
    return <p className="text-sm text-neutral-500 text-center py-4">読み込み中...</p>;
  }

  if (!order) {
    return <p className="text-sm text-neutral-500 text-center py-4">発注が見つかりません</p>;
  }

  const supplierName = order.supplier?.partner_name ?? '';

  return (
    <div className="flex flex-col min-h-[calc(100vh-44px)] gap-3">
      {/* ヘッダー */}
      <div className="bg-white flex gap-2 items-center px-2 py-1">
        <Link to="/receiving" className="shrink-0">
          <ArrowLeft className="size-5" />
        </Link>
        <div className="flex flex-col min-w-0">
          <p className="text-sm">発注No.{order.id}</p>
          {supplierName && <p className="-mt-1">{supplierName}</p>}
        </div>
      </div>

      {/* コンテンツ */}
      <div className="flex-1 flex flex-col gap-2 pb-3 px-5">
        {/* 未入荷 */}
        {unreceivedItems.length > 0 && (
          <>
            <h3 className="border-b border-neutral-400 pb-1">未入荷</h3>
            <div className="flex flex-col gap-2">
              {unreceivedItems.map((item) => (
                <ItemCard
                  key={item.id}
                  item={item}
                  receivingQty={receivingQuantities[item.id] ?? 0}
                  onReceivingQtyChange={(qty) => updateReceivingQty(item.id, qty)}
                />
              ))}
            </div>
          </>
        )}

        {/* 入荷済 */}
        {receivedItems.length > 0 && (
          <>
            <h3 className="border-b border-neutral-400 pb-1 mt-2">入荷済</h3>
            <div className="flex flex-col gap-2">
              {receivedItems.map((item) => (
                <ItemCard key={item.id} item={item} />
              ))}
            </div>
          </>
        )}
      </div>

      {/* フッターボタン */}
      {unreceivedItems.length > 0 && (
        <div className="sticky bottom-0 bg-white flex gap-3 px-5 py-2">
          <button
            type="button"
            disabled={!hasReceivedItems || isDelivering}
            onClick={handleReceivingComplete}
            className={`flex-1 py-2 text-lg font-bold text-white ${
              hasReceivedItems && !isDelivering ? 'bg-green-600' : 'bg-gray-400 opacity-50'
            }`}
          >
            {isDelivering ? '処理中...' : '入荷完了'}
          </button>
        </div>
      )}
    </div>
  );
};
